import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGift } from '@fortawesome/free-solid-svg-icons';
import raffleService from '../../../../../../../services/raffle/service';
import { useStore } from '../../../../../../../hooks/storeContext';
import { 
    RaffleCardContainer, 
    RaffleImageSection, 
    RaffleContent, 
    RaffleTitle, 
    RaffleStatsRow, 
    InfoBox,
    EnterRaffleButton 
} from './styled';

const RaffleCardComponent = ({ item, theme }) => {
    const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0 });
    const [isExpired, setIsExpired] = useState(false);
    const [fetchedRaffleData, setFetchedRaffleData] = useState(null);
    const [isLoadingRaffleData, setIsLoadingRaffleData] = useState(true);
    const { authStore } = useStore();

    const handleRaffleClick = () => {
        // Navigate to raffle checkout page using the checkoutUrl from the backend
        const raffleUrl = item.checkoutUrl;
        
        if (!raffleUrl) {
            console.warn('RaffleCardComponent - No checkoutUrl available for raffle:', item);
            return;
        }
        
        window.location.href = raffleUrl;
    };

    // Extract store name and product slug from checkout URL for API call
    const extractUrlInfo = (checkoutUrl) => {
        try {
            // URL format: http://sparkle.store:3000/litt/checkout/dodge-charger-Xw54MpMG
            const url = new URL(checkoutUrl);
            const pathParts = url.pathname.split('/');
            const storeName = pathParts[1]; // 'litt'
            const productSlug = pathParts[3]; // 'dodge-charger-Xw54MpMG'
            
            return { storeName, productSlug };
        } catch (error) {
            console.error('Error parsing checkout URL:', error);
            return null;
        }
    };

    // Fetch raffle data when component mounts
    useEffect(() => {
        const fetchRaffleData = async () => {
            if (!item.checkoutUrl) {
                setIsLoadingRaffleData(false);
                return;
            }

            try {
                const urlInfo = extractUrlInfo(item.checkoutUrl);
                if (!urlInfo) {
                    setIsLoadingRaffleData(false);
                    return;
                }

                const { storeName, productSlug } = urlInfo;
                
                // First get the checkout data to get the real productId
                const checkoutData = await authStore.getCheckoutData(storeName, productSlug);
                if (!checkoutData?.checkoutPage?.productId) {
                    console.error('No productId found in checkout data');
                    setIsLoadingRaffleData(false);
                    return;
                }

                const productId = checkoutData.checkoutPage.productId;
                
                // Now fetch the raffle data using the real productId
                const raffleData = await raffleService.getRafflePreview(storeName, productId);
                
                setFetchedRaffleData(raffleData);
            } catch (error) {
                console.error('Failed to fetch raffle data:', error);
            } finally {
                setIsLoadingRaffleData(false);
            }
        };

        fetchRaffleData();
    }, [item.checkoutUrl, authStore]);

    // Use fetched raffle data or fallback to item data
    let raffleData = {};
    
    if (fetchedRaffleData) {
        raffleData = fetchedRaffleData;
    } else {
        // Fallback to parsing from item data (original logic)
        try {
            if (typeof item.raffleData === 'string' && item.raffleData.trim()) {
                raffleData = JSON.parse(item.raffleData);
            } else if (typeof item.raffleData === 'object' && item.raffleData !== null) {
                raffleData = item.raffleData;
            }
            // Check if raffle data is in a different field
            else if (item.raffle) {
                raffleData = item.raffle;
            }
        } catch (error) {
            console.error('RaffleCardComponent - Error parsing raffleData:', error);
            raffleData = {};
        }
    }
    
    // Update countdown timer
    useEffect(() => {
        if (!raffleData.endDate) {
            return;
        }

        const updateCountdown = () => {
            const now = new Date().getTime();
            const endTime = new Date(raffleData.endDate).getTime();
            const timeRemaining = endTime - now;

            if (timeRemaining <= 0) {
                setCountdown({ days: 0, hours: 0, minutes: 0 });
                setIsExpired(true);
                return;
            }

            const days = Math.floor(timeRemaining / (1000 * 60 * 60 * 24));
            const hours = Math.floor((timeRemaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const minutes = Math.floor((timeRemaining % (1000 * 60 * 60)) / (1000 * 60));
            setCountdown({ days, hours, minutes });
            setIsExpired(false);
        };

        updateCountdown();
        const interval = setInterval(updateCountdown, 60000); // Update every minute

        return () => clearInterval(interval);
    }, [raffleData.endDate, fetchedRaffleData]); // Also depend on fetchedRaffleData to trigger when data is loaded

    // Get featured image (prioritize featuredImage over gallery images)
    const prizeImage = raffleData.featuredImage || 
        (raffleData.prizeImages && raffleData.prizeImages.length > 0 ? raffleData.prizeImages[0] : null) ||
        item.thumbnail || item.image || item.featuredImage || item.checkoutImage;

    // Format price for display
    const formatPrice = (price) => {
        if (!price) return null;
        const numPrice = parseFloat(price);
        if (isNaN(numPrice)) return null;
        return numPrice === 0 ? 'Free Entry' : `$${numPrice.toFixed(2)}`;
    };

    const ticketPrice = formatPrice(raffleData.ticketPrice || item.price);

    // Format countdown display
    const formatCountdown = () => {
        if (isLoadingRaffleData) return 'Loading...';
        if (isExpired) return 'Ended';
        
        if (countdown.days > 0) {
            return `${countdown.days}d ${countdown.hours}h remaining`;
        } else if (countdown.hours > 0) {
            return `${countdown.hours}h ${countdown.minutes}m remaining`;
        } else {
            return `${countdown.minutes}m remaining`;
        }
    };

    return (
        <RaffleCardContainer theme={theme} onClick={handleRaffleClick} isExpired={isExpired}>
            <RaffleImageSection theme={theme}>
                {prizeImage ? (
                    <>
                        <img 
                            src={prizeImage} 
                            alt={raffleData.prizeTitle || item.text || item.title || 'Raffle Prize'} 
                            className="raffle-thumbnail"
                        />
                        <div className="image-overlay" />
                        <RaffleTitle theme={theme}>
                            {raffleData.prizeTitle || item.text || item.title || 'Untitled Raffle'}
                        </RaffleTitle>
                    </>
                ) : (
                    <>
                        <div className="raffle-hero-bg">
                            <div className="gradient-overlay"></div>
                            <div className="raffle-icon">
                                <FontAwesomeIcon icon={faGift} />
                            </div>
                        </div>
                        <RaffleTitle theme={theme}>
                            {raffleData.prizeTitle || item.text || item.title || 'Untitled Raffle'}
                        </RaffleTitle>
                    </>
                )}
            </RaffleImageSection>
            
            <RaffleContent>
                {/* Info boxes layout like in the screenshot */}
                <RaffleStatsRow>
                    <InfoBox theme={theme}>
                        <div className="label">Price per ticket</div>
                        <div className="value">{ticketPrice || '$0.00'}</div>
                    </InfoBox>
                    
                    <InfoBox theme={theme} isExpired={isExpired}>
                        <div className="label">Ends in</div>
                        <div className={`value countdown`}>
                            {formatCountdown()}
                        </div>
                    </InfoBox>
                </RaffleStatsRow>
                
                <EnterRaffleButton 
                    onClick={(e) => {
                        e.stopPropagation();
                        handleRaffleClick();
                    }}
                    theme={theme}
                    disabled={isExpired}
                >
                    {isExpired ? 'Raffle Ended' : 'Enter Raffle'}
                </EnterRaffleButton>
            </RaffleContent>
        </RaffleCardContainer>
    );
};

export default RaffleCardComponent;
